// UI strings for speaking together (docs/PEDAGOGY.md §3–4, 2026-10-06): the "Puhutaan" block of
// Yhteinen sessio (the speaking ladder, together/speaking/) and the everyday scenes of our own in
// the role play (together/everyday/). Finnish (default) and English side by side, the same shape.
import type { LadderStepId } from '@valyrio/engine/speaking-ladder';
import { defineArea } from '../area';

/** A person's name in the genitive (Roosa → Roosan, Axel → Axelin; docs/FINNISH-STYLE.md "Nimien taivutus"). */
const fiGen = (name: string) => (/[aeiouyäöå]$/i.test(name) ? `${name}n` : `${name}in`);
const fiSessions = (n: number) => (n === 1 ? 'vielä yksi istunto, jossa' : `vielä ${n} istuntoa, joissa`);

export const speakingFi = {
  title: 'Puhutaan',
  stepOf: (n: number) => `Askel ${n}/5`,
  steps: {
    aloud: 'Ääneen yhdessä',
    tell: 'Kuuntele ja kerro',
    answer: 'Kuuntele ja vastaa',
    guided: 'Ohjattu keskustelu',
    free: 'Vapaasti ja hahmossa',
  } as Record<LadderStepId, string>,
  /** One line under the step's name: what the two of you do. */
  how: {
    aloud: 'Kuunnelkaa kerran, lukekaa vuorotellen ääneen.',
    tell: 'Toinen lukee, toinen kertoo suomeksi, mitä kuuli.',
    answer: 'Kysyjä sanoo lauseen, toinen vastaa valyriaksi.',
    guided: 'Kummallakin on oma korttinsa. Kysykää ja kertokaa valyriaksi, kunnes tiedätte molemmat saman.',
    free: 'Puhukaa samasta aiheesta kolme kertaa, joka kerta lyhyemmin.',
  } as Record<LadderStepId, string>,
  rowItems: (n: number) => `${n} ${n === 1 ? 'vuoro' : 'vuoroa'}`,
  rowDone: 'Tehty tältä viikolta',
  start: 'Aloita',
  again: 'Käy läpi uudelleen',
  warmup: 'Lämmittely',
  warmupOf: (step: string) => `Lämmittely: ${step}`,
  progress: (i: number, n: number) => `Vuoro ${i}/${n}`,
  prev: 'Edellinen',
  next: 'Seuraava',
  finish: 'Valmis',
  listenFirst: 'Kuuntele ensin',
  readerYou: 'Sinä luet',
  readerPartner: (name: string) => `${name} lukee`,
  askerYou: 'Sinä kysyt',
  askerPartner: (name: string) => `${name} kysyy`,
  // step 1
  markPrompt: 'Kuulostiko jokin sana oudolta? Napauta sitä.',
  markWord: (w: string) => `Merkitse: ${w}`,
  wentWell: 'Sujui hyvin',
  // step 2
  readAloud: 'Lue lause ääneen. Toista, jos kuuntelija pyytää.',
  listenTell: (name: string) => `Kuuntele, mitä ${name} lukee, ja kerro suomeksi, mitä lause tarkoittaa.`,
  reveal: 'Näytä lause',
  understood: 'Ymmärsin',
  partly: 'Osittain',
  notUnderstood: 'En ymmärtänyt',
  // step 3
  askThis: 'Kysy tämä. Vastauskortti on kuuntelijan ruudulla.',
  listenAnswer: (name: string) => `Kuuntele, mitä ${name} kysyy, ja vastaa valyriaksi.`,
  card: 'Vastauskortti',
  model: 'Mallivastaus',
  showModel: 'Näytä kysymys ja malli',
  fits: 'Sopi',
  almost: 'Melkein',
  notYet: 'Ei vielä',
  howDidItGo: (name: string) => `Miten ${name} vastasi?`,
  /** Where a line comes from (never invented High Valyrian). */
  source: {
    example: 'Petersonin esimerkki',
    show: 'sarjan repliikki',
    lesson: (n: number) => `harjoituslause, oppitunti ${n}`,
    word: 'sanakirjan sana',
    stack: 'Lausepinon lause',
  },
  // after the last item
  doneHead: 'Tämän viikon puhuminen',
  scoreAloud: (name: string, marked: number, n: number) => `${name}: merkittyjä lauseita ${marked}/${n}`,
  scoreTell: (name: string, ok: number, n: number) => `${name} ymmärsi ${ok}/${n}`,
  scoreAnswer: (name: string, ok: number, n: number) => `${name} vastasi sopivasti ${ok}/${n}`,
  // the ladder (PEDAGOGY §3.2–3.3)
  ladder: 'Puhumisen askeleet',
  here: 'olette tässä',
  passed: 'takana',
  opensAfter: (n: number) => `avautuu oppitunnin ${n} jälkeen`,
  laterInApp: 'tulee sovellukseen myöhemmin',
  ask: (step: string) => `Valmiita seuraavaan askeleeseen: ${step}?`,
  yes: 'Kyllä',
  notYetAnswer: 'Vielä ei',
  waitingFor: (name: string) => `Odotetaan ${fiGen(name)} vastausta.`,
  saidNo: 'Palataan tähän ensi viikolla.',
  needs: {
    aloud: (n: number) => `Seuraavaan askeleeseen: ${fiSessions(n)} kumpikin lukee niin, että merkittyjä lauseita on enintään joka neljäs.`,
    tell: (n: number) => `Seuraavaan askeleeseen: ${fiSessions(n)} molemmat ymmärtävät 8/10.`,
    answer: (n: number) => `Seuraavaan askeleeseen: ${fiSessions(n)} molemmat vastaavat 7/10 sopivasti.`,
    guided: (n: number) =>
      n === 1
        ? 'Seuraavaan askeleeseen: vielä yksi tehtävä, jossa kumpikin avaa enintään kaksi mallilausetta.'
        : `Seuraavaan askeleeseen: vielä ${n} tehtävää peräkkäin, joissa kumpikin avaa enintään kaksi mallilausetta.`,
    free: (_n: number) => '',
  } as Record<LadderStepId, (n: number) => string>,
  personNeeds: (name: string, n: number) => `${name}: ${n === 1 ? 'vielä yksi istunto' : `vielä ${n} istuntoa`}`,
  personReady: (name: string) => `${name}: valmis`,
  afterLesson: (n: number) => `Seuraava askel avautuu, kun olette molemmat läpäisseet oppitunnin ${n}.`,
  notBuilt: 'Seuraava askel tulee sovellukseen myöhemmin.',
  top: 'Tämä on ylin askel. Alemmat askeleet jäävät lämmittelyksi.',
  stepBack: 'Otetaanko välillä edellinen askel? Se vahvistaa pohjaa.',
  stepBackDo: 'Harjoitellaan tänään edellistä askelta',
  stepBackUndo: 'Takaisin tämän viikon askeleeseen',
  practisingPrev: (step: string) => `Tänään: ${step}`,
  noPartner: 'Puhuminen tehdään yhdessä. Kumppanin tiliä ei ole vielä.',
  empty: 'Lauseita ei vielä ole. Ne tulevat oppitunneilta, jotka olette läpäisseet.',
  // everyday scenes of our own (role play)
  oursGroup: 'Omat arkikohtaukset',
  oursNote: 'Oma arkikohtaus, ei sarjasta eikä Petersonin. Lauseet ovat oppitunneilta ja sanakirjasta.',
  example: 'malli',
  exampleOf: (who: string) => `Malli: ${who}`,
  goodAsExample: 'Kelpaa malliksi',
  youProposed: 'Ehdotit malliksi',
  partnerProposed: (name: string) => `${name} ehdottaa malliksi`,
  replaceExample: 'Vaihda malliksi',
  playExample: 'Kuuntele malli',
  noExample: 'Ei vielä mallia',
};

export type SpeakingMessages = typeof speakingFi;

const enSessions = (n: number) => (n === 1 ? 'one more session' : `${n} more sessions`);

export const speakingEn: SpeakingMessages = {
  title: 'Let’s talk',
  stepOf: (n) => `Step ${n}/5`,
  steps: {
    aloud: 'Aloud together',
    tell: 'Listen and tell',
    answer: 'Listen and answer',
    guided: 'Guided conversation',
    free: 'Freely and in character',
  },
  how: {
    aloud: 'Listen once, then read aloud in turns.',
    tell: 'One reads, the other says in Finnish what they heard.',
    answer: 'The asker says the line, the other answers in High Valyrian.',
    guided: 'Each of you has a card of your own. Ask and tell in High Valyrian until you find out.',
    free: 'Talk about the same topic three times, shorter each time.',
  },
  rowItems: (n) => `${n} ${n === 1 ? 'turn' : 'turns'}`,
  rowDone: 'Done this week',
  start: 'Start',
  again: 'Go through again',
  warmup: 'Warm-up',
  warmupOf: (step) => `Warm-up: ${step}`,
  progress: (i, n) => `Turn ${i}/${n}`,
  prev: 'Previous',
  next: 'Next',
  finish: 'Done',
  listenFirst: 'Listen first',
  readerYou: 'You read',
  readerPartner: (name) => `${name} reads`,
  askerYou: 'You ask',
  askerPartner: (name) => `${name} asks`,
  markPrompt: 'Did a word sound off? Tap it.',
  markWord: (w) => `Mark: ${w}`,
  wentWell: 'Went well',
  readAloud: 'Read the line aloud. Repeat it if the listener asks.',
  listenTell: (name) => `Listen to what ${name} reads and say in Finnish what it means.`,
  reveal: 'Show the line',
  understood: 'Understood',
  partly: 'Partly',
  notUnderstood: 'Did not understand',
  askThis: 'Ask this. The answer card is on the listener’s screen.',
  listenAnswer: (name) => `Listen to what ${name} asks and answer in High Valyrian.`,
  card: 'Answer card',
  model: 'Model answer',
  showModel: 'Show the question and the model',
  fits: 'Fits',
  almost: 'Almost',
  notYet: 'Not yet',
  howDidItGo: (name) => `How did ${name} answer?`,
  source: {
    example: 'Peterson’s example',
    show: 'show line',
    lesson: (n) => `practice sentence, lesson ${n}`,
    word: 'dictionary word',
    stack: 'sentence stack sentence',
  },
  doneHead: 'This week’s speaking',
  scoreAloud: (name, marked, n) => `${name}: lines marked ${marked}/${n}`,
  scoreTell: (name, ok, n) => `${name} understood ${ok}/${n}`,
  scoreAnswer: (name, ok, n) => `${name} answered fittingly ${ok}/${n}`,
  ladder: 'Speaking steps',
  here: 'you are here',
  passed: 'behind you',
  opensAfter: (n) => `opens after lesson ${n}`,
  laterInApp: 'comes to the app later',
  ask: (step) => `Ready for the next step: ${step}?`,
  yes: 'Yes',
  notYetAnswer: 'Not yet',
  waitingFor: (name) => `Waiting for ${name}’s answer.`,
  saidNo: 'We come back to this next week.',
  needs: {
    aloud: (n) => `Next step: ${enSessions(n)} where each of you reads with at most one line in four marked.`,
    tell: (n) => `Next step: ${enSessions(n)} where both of you understand 8/10.`,
    answer: (n) => `Next step: ${enSessions(n)} where both of you answer 7/10 fittingly.`,
    guided: (n) => `Next step: ${n === 1 ? 'one more task' : `${n} more tasks`} in a row where each of you opens at most two model phrases.`,
    free: () => '',
  },
  personNeeds: (name, n) => `${name}: ${enSessions(n)}`,
  personReady: (name) => `${name}: ready`,
  afterLesson: (n) => `The next step opens once you have both passed lesson ${n}.`,
  notBuilt: 'The next step comes to the app later.',
  top: 'This is the top step. The lower steps stay as warm-ups.',
  stepBack: 'Take the previous step for a while? It strengthens the ground.',
  stepBackDo: 'Practise the previous step today',
  stepBackUndo: 'Back to this week’s step',
  practisingPrev: (step) => `Today: ${step}`,
  noPartner: 'Speaking is done together. There is no partner account yet.',
  empty: 'No lines yet. They come from the lessons you have passed.',
  oursGroup: 'Everyday scenes of ours',
  oursNote: 'An everyday scene of our own, not from the show and not Peterson’s. The lines come from the lessons and the dictionary.',
  example: 'model',
  exampleOf: (who) => `Model: ${who}`,
  goodAsExample: 'Good as the model',
  youProposed: 'You proposed it as the model',
  partnerProposed: (name) => `${name} proposes it as the model`,
  replaceExample: 'Make it the model',
  playExample: 'Hear the model',
  noExample: 'No model yet',
};

// Messages.speaking (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('speaking', speakingFi, speakingEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof speakingFi> {}
}
